import * as React from 'react';
import { FormControl, FormHelperText, InputLabel, MenuItem, Select } from '@mui/material';
import styled, { css } from 'styled-components';
import { useController, useFormContext } from 'react-hook-form';
import { RadioGroupChangeEvent } from '@progress/kendo-react-inputs';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FieldProps, generateValidations, MaterialUiFieldProps } from '../../core/form';
import {
	borderGrey,
	brandingColourHeavy,
	errorRed,
	labelGrey,
	whiteBackground
} from '../../appComponents/cssConstants';
import { DropDownListOption } from '../../models/DropDownListOption';
import { hasValue } from '../../lib/lib';
import BottomListModal from '../bottomModal/BottomListModal';

const DropdownListWithModalField = ({
	label,
	modalTitle,
	name,
	data,
	displayInLineLabel = true,
	className = '',
	disabled,
	required = false,
	validate = null
}: Props) => {
	const {
		formState: { errors }
	} = useFormContext();
	const { field } = useController({ name, rules: generateValidations({ required, validate } as FieldProps) });
	const labelId = `${name}Label`;
	const hasError = hasValue(errors[name]);
	const { value } = field;

	const [open, setOpen] = React.useState(false);

	const toggleDrawer = (newOpen: boolean) => () => {
		setOpen(newOpen);
	};

	const handleChange = (e: RadioGroupChangeEvent) => {
		setOpen(false);
		field.onChange(hasValue(e.value) ? e.value : null);
	};

	return (
		<>
			<StyledFormControl
				variant="filled"
				fullWidth
				size="small"
				className={className}
				$displayInLineLabel={displayInLineLabel}
			>
				{label && (
					<StyledLabel id={labelId} error={hasError} $displayInLineLabel={displayInLineLabel}>
						{label}
					</StyledLabel>
				)}
				<StyledSelect
					name={name}
					id={name}
					labelId={labelId}
					value={value ?? ''}
					open={false} // To stop default dropdown
					onOpen={toggleDrawer(true)}
					className={hasError && 'error'}
					$displayInLineLabel={displayInLineLabel}
					disabled={disabled}
					IconComponent={ExpandMoreIcon}
				>
					{data?.map((x) => (
						<MenuItem key={x.value} value={x.value}>
							{x.text}
						</MenuItem>
					))}
				</StyledSelect>
				<FormHelperText error={hasError}>{errors[name]?.message as React.ReactNode}</FormHelperText>
			</StyledFormControl>

			<BottomListModal
				open={open}
				onClose={toggleDrawer(false)}
				title={modalTitle ?? label}
				data={data}
				name="businessIndustry"
				value={value ?? ''}
				onChange={handleChange}
			/>
		</>
	);
};

export default DropdownListWithModalField;

interface Props extends MaterialUiFieldProps {
	data: DropDownListOption<string | number>[];
	displayInLineLabel?: boolean;
	modalTitle?: string;
}

const StyledFormControl = styled(FormControl)<{ $displayInLineLabel?: boolean }>`
	margin-bottom: 10px;
	vertical-align: ${(props) => (props.$displayInLineLabel ? 'top' : 'bottom')};

	.MuiFormLabel-root {
		padding: 3px;
	}

	.MuiFormHelperText-root {
		&.Mui-error {
			font-size: 10px;
			font-weight: 500;
			margin: 5px 0 0 0;
			border-color: ${errorRed};
			color: ${errorRed};
		}
	}
`;

const StyledLabel = styled(InputLabel)<{ $displayInLineLabel: boolean }>`
	color: ${labelGrey};

	&.Mui-focused {
		color: ${brandingColourHeavy};
	}

	&.Mui-error {
		color: ${errorRed};
	}

	${(props) =>
		!props.$displayInLineLabel &&
		css`
			position: relative;
			max-width: 100%;
			transform: none;
			white-space: initial;
			margin: 6px 0;
		`}
`;

const StyledSelect = styled(Select)<{ $displayInLineLabel: boolean }>`
	border: 1px solid ${borderGrey};
	border-radius: 12px;
	background-color: ${whiteBackground};
	font-size: 16px;
	color: black;

	&:before,
	&:after {
		display: none;
	}

	&:hover:not(.Mui-disabled) {
		border-color: ${labelGrey};
		background-color: white;
	}

	&.Mui-focused {
		border-color: ${brandingColourHeavy};
		background-color: white;
		border-width: 1px;
	}

	&.error {
		border-color: ${errorRed};
	}

	.MuiInputBase-input {
		text-align: left;

		${(props) =>
			!props.$displayInLineLabel &&
			css`
				padding-top: 12px;
				padding-bottom: 13px;
			`}
	}
`;
